import { Link } from '@tanstack/react-router';
import { BookOpen } from 'lucide-react';
import { CompassLogo } from '@jeraisonne/ui';
import type { BlogArticleSummary } from '@jeraisonne/db';
import { formatRelativeDate } from './format';

/**
 * Carte « article de blog » du fil d'actu. Mirroir structurel de
 * `assignment-card.tsx` (fond `bg-white` + textes sur l'échelle `ink`, qui
 * suit le thème : clair sur verre en `.theme-ocean`, bleu nuit sur blanc en
 * `.theme-student`). Toute la carte est le lien (pas de bouton CTA séparé) — la
 * source est éditoriale JeRaisonne, pas un auteur ciblant le viewer comme un
 * devoir, donc pas de jointure `authorsById`.
 */
export function BlogArticleCard({ data }: { data: BlogArticleSummary }) {
  return (
    <Link
      to="/blog/$slug"
      params={{ slug: data.slug }}
      className="block overflow-hidden rounded-2xl bg-white transition-transform active:scale-[0.98]"
    >
      <div className="flex items-center gap-2.5 px-4 py-3">
        <CompassLogo size="sm" showWordmark={false} animated={false} className="shrink-0" />
        <div className="min-w-0 flex-1">
          <span className="text-ink-900 font-sans text-xs font-semibold">JeRaisonne</span>
          <span className="text-ink-500 font-sans text-xs"> a publié un article</span>
        </div>
        <span className="text-ink-400 shrink-0 font-sans text-[11px]">
          {formatRelativeDate(data.published_at ?? data.created_at)}
        </span>
      </div>

      <div className="flex gap-3 p-4">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="mb-1 flex flex-wrap items-center gap-1.5">
            <BookOpen className="text-primary-400 h-3.5 w-3.5 shrink-0" />
            <span className="text-primary-600 font-sans text-[11px] font-semibold uppercase tracking-wider">
              Article
            </span>
            <span className="bg-ink-100 text-ink-500 ml-auto shrink-0 rounded-full px-2.5 py-0.5 font-sans text-[11px] font-semibold">
              {data.reading_time} min
            </span>
          </div>
          <p className="text-ink-900 font-sans text-sm font-semibold leading-snug">{data.title}</p>
          {data.subtitle && (
            <p className="text-ink-600 mt-0.5 line-clamp-2 font-sans text-xs">{data.subtitle}</p>
          )}
        </div>

        {data.illustration_url ? (
          <img
            src={data.illustration_url}
            alt=""
            loading="lazy"
            aria-hidden="true"
            className="bg-ink-50 h-20 w-20 shrink-0 rounded-xl object-cover sm:h-24 sm:w-24"
          />
        ) : (
          <div
            aria-hidden="true"
            className="from-primary-100 to-primary-300 bg-linear-to-br flex h-20 w-20 shrink-0 items-center justify-center rounded-xl sm:h-24 sm:w-24"
          >
            <BookOpen className="text-primary-500 h-6 w-6" />
          </div>
        )}
      </div>
    </Link>
  );
}
